import * as React from 'react';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { Box, Stack } from 'src/components-v2/Layout';
import { MediaSwitch } from 'src/components-v2/Media';
import Bullet from './Bullet';
import { ITimelineItemProps } from './Timeline.types';
import { useCmsTheme } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';
import { CmsThemes } from 'src/lib/constants';
import { isAutomatedBrowser } from 'src/lib/browser-utils';

const TimelineItem: React.FunctionComponent<ITimelineItemProps> = ({
  index,
  item,
  items,
  refs,
  timelineRefBounds,
}) => {
  const inspectorProps = useContentfulInspectorMode({ entryId: item?.sys?.id });
  const cmsTheme = useCmsTheme();
  const isV2 = cmsTheme === CmsThemes.WEB2;

  return (
    <Stack
      key={`step-${index}`}
      sx={{
        minHeight: {
          // The last-item calc reads the ref during render, which is
          // unpopulated on the first pass — it only takes effect if a later
          // re-render happens to re-evaluate it, a race that flipped captured
          // page heights. Automated browsers use the plain step height.
          md:
            index === items.length - 1 && !isAutomatedBrowser()
              ? `calc((100vh - ${refs.current[index]?.clientHeight}px) / 2 + ${refs.current[index]?.clientHeight}px)`
              : '100vh',
          xs: 'auto',
        },
        justifyContent: index === items.length - 1 ? 'flex-end' : 'center',
        ...items?.[index]?.sx,
      }}
    >
      <Box
        id={item.id}
        ref={(el) => {
          refs.current[index] = el as HTMLDivElement;
        }}
        {...inspectorProps({
          fieldId: 'internalTitle',
        })}
      >
        {isV2 && <Bullet bounds={timelineRefBounds} />}
        {/* Mobile-only asset: rendered above the content to match Figma
            (SITE-6128). Hidden on desktop, where the sticky asset column
            handles the media instead. */}
        <MediaSwitch
          key={`mobile-asset-${index}`}
          asset={{ ...items[index].asset }}
        />
        {item.content}
      </Box>
    </Stack>
  );
};

export default TimelineItem;
